import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { FilmStrip, CaretDown, CaretUp, CheckCircle, XCircle, VideoCamera, Gift, EnvelopeSimple } from '@phosphor-icons/react';

const STATUS_CLS = {
  queued: 'bg-blue-50 text-blue-700 border-blue-200',
  recording: 'bg-amber-50 text-amber-700 border-amber-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-red-50 text-red-600 border-red-200',
};

export default function VideoCrunchJobsAdminPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState('');
  const [publishForm, setPublishForm] = useState({ video_url: '', actual_seconds: '' });
  const [failReason, setFailReason] = useState('');
  const [digestBusy, setDigestBusy] = useState(false);

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post('/vcx/admin/digest/send-now');
      toast.success(data.sent ? `Digest emailed to ${data.to}` : `Not sent: ${data.reason}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Digest send failed');
    }
    setDigestBusy(false);
  };

  const load = async () => {
    try {
      const { data } = await api.get('/vcx/admin/jobs');
      setJobs(data.jobs);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load jobs');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const update = async (jobId, body) => {
    try {
      const { data } = await api.put(`/vcx/admin/jobs/${jobId}`, body);
      setJobs((prev) => prev.map((j) => (j.job_id === jobId ? data.job : j)));
      if (body.status === 'published') {
        toast.success(data.email_sent
          ? 'Published — customer notified by email 🎬'
          : 'Published (email notification could not be sent — tell the customer manually)');
      } else {
        toast.success(`Job ${body.status}`);
      }
      setPublishForm({ video_url: '', actual_seconds: '' });
      setFailReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const active = jobs.filter((j) => ['queued', 'recording'].includes(j.status));
  const done = jobs.filter((j) => ['published', 'failed'].includes(j.status));

  const JobCard = ({ j }) => {
    const isOpen = open === j.job_id;
    return (
      <div className="border border-slate-200 rounded-lg bg-white" data-testid={`admin-vcx-job-${j.job_id}`}>
        <button onClick={() => setOpen(isOpen ? '' : j.job_id)} className="w-full flex items-center gap-3 p-4 text-left">
          <span className={`text-[11px] font-black px-2.5 py-1 rounded-full border ${STATUS_CLS[j.status]}`}>{j.status}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">{j.title}</p>
            <p className="text-xs text-slate-400">{j.customer_email} · target {j.target_minutes} min · {j.is_free_trial ? 'FREE TRIAL' : `${j.estimated_credits} cr reserved`} · {new Date(j.created_at).toLocaleString()}</p>
          </div>
          {j.is_free_trial && <Gift size={16} className="text-emerald-500 shrink-0" />}
          {isOpen ? <CaretUp size={16} className="text-slate-400" /> : <CaretDown size={16} className="text-slate-400" />}
        </button>
        {isOpen && (
          <div className="border-t border-slate-100 p-4 space-y-3 text-sm">
            <p><span className="font-bold text-slate-500 text-xs uppercase">App URL:</span> <a href={j.app_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{j.app_url}</a></p>
            {j.demo_login && <p><span className="font-bold text-slate-500 text-xs uppercase">Demo login:</span> {j.demo_login} / {j.demo_password || '—'}</p>}
            <p className="whitespace-pre-wrap text-slate-700 bg-slate-50 border border-slate-100 rounded p-3 text-xs">{j.steps_text}</p>
            {j.status === 'queued' && (
              <button onClick={() => update(j.job_id, { status: 'recording' })}
                className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-2 rounded-lg"
                data-testid={`admin-vcx-start-${j.job_id}`}>
                <VideoCamera size={14} weight="bold" />Start recording
              </button>
            )}
            {['queued', 'recording'].includes(j.status) && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="flex flex-wrap gap-2">
                  <input type="text" value={publishForm.video_url} onChange={(e) => setPublishForm({ ...publishForm, video_url: e.target.value })}
                    placeholder="Published video URL (.mp4)" className="flex-1 min-w-[240px] border border-slate-300 rounded-lg px-3 py-2 text-xs"
                    data-testid={`admin-vcx-url-${j.job_id}`} />
                  <input type="number" value={publishForm.actual_seconds} onChange={(e) => setPublishForm({ ...publishForm, actual_seconds: e.target.value })}
                    placeholder="Seconds" className="w-24 border border-slate-300 rounded-lg px-3 py-2 text-xs"
                    data-testid={`admin-vcx-seconds-${j.job_id}`} />
                  <button onClick={() => update(j.job_id, { status: 'published', video_url: publishForm.video_url, actual_seconds: parseInt(publishForm.actual_seconds || '0') })}
                    className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-lg"
                    data-testid={`admin-vcx-publish-${j.job_id}`}>
                    <CheckCircle size={14} weight="bold" />Publish
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input type="text" value={failReason} onChange={(e) => setFailReason(e.target.value)}
                    placeholder="Fail reason (customer sees this; credits auto-refund)" className="flex-1 min-w-[240px] border border-slate-300 rounded-lg px-3 py-2 text-xs"
                    data-testid={`admin-vcx-failreason-${j.job_id}`} />
                  <button onClick={() => update(j.job_id, { status: 'failed', fail_reason: failReason })}
                    className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 text-xs font-bold px-3 py-2 rounded-lg"
                    data-testid={`admin-vcx-fail-${j.job_id}`}>
                    <XCircle size={14} weight="bold" />Fail + refund
                  </button>
                </div>
              </div>
            )}
            {j.status === 'published' && (
              <p className="text-xs text-emerald-600 font-bold">
                Published: {j.video_url} ({j.actual_seconds}s = {j.actual_credits} credits)
                {j.customer_notified_at && <span className="ml-2 text-emerald-500 font-semibold" data-testid={`admin-vcx-notified-${j.job_id}`}>· customer emailed ✓</span>}
              </p>
            )}
            {j.status === 'failed' && <p className="text-xs text-red-500">Failed: {j.fail_reason || 'no reason recorded'} — credits refunded</p>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="admin-vcx-page">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
          <FilmStrip size={22} weight="bold" className="text-[#F39C12]" />VideoCrunch Production Queue
          <button onClick={sendDigest} disabled={digestBusy}
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 border border-slate-300 hover:bg-slate-50 disabled:opacity-40 px-3 py-2 rounded-lg"
            data-testid="admin-vcx-digest-btn" title="Email yourself this week's VideoCrunch numbers now (also sent automatically every Monday 7 AM ET)">
            <EnvelopeSimple size={14} weight="bold" />{digestBusy ? 'Sending…' : 'Email me the weekly digest'}
          </button>
        </h1>
        <p className="text-sm text-slate-500 mt-1">Customer video requests from /videocrunch/app. Publish deducts actual credits (1 credit = 6 seconds); failing refunds the hold automatically. Brand before publishing: free trial → <code className="bg-slate-100 px-1 rounded">brand.py --watermark --endcard</code>, paid → <code className="bg-slate-100 px-1 rounded">brand.py --endcard</code>.</p>
      </div>
      {loading ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="space-y-8">
          <section>
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-500 mb-3">To produce ({active.length})</h2>
            {active.length === 0 ? <p className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg p-4">Queue is empty.</p>
              : <div className="space-y-3">{active.map((j) => <JobCard key={j.job_id} j={j} />)}</div>}
          </section>
          {done.length > 0 && (
            <section>
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-400 mb-3">Settled ({done.length})</h2>
              <div className="space-y-3 opacity-70">{done.map((j) => <JobCard key={j.job_id} j={j} />)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
